import { useState } from 'react';
import { Clock, Trash2 } from 'lucide-react';
import type { TimeEntryWithContext } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatDate, formatEuro } from '@/lib/utils';

/**
 * Erfasste Zeit am Projekt, daneben Budget und Beauftragtes.
 *
 * Bewusst ohne Stoppuhr: eine laufende Uhr will bedient werden, und eine, die
 * jemand zu stoppen vergisst, schreibt Unsinn in die Summe. Eingetragen wird
 * hinterher, in Minuten.
 *
 * Minuten und nicht Kommastunden, weil „90“ eindeutig ist und „1,5“ beim
 * Eintippen leicht zu „15“ wird.
 */
export function TimeCard({
  projectId,
  entries,
  budgetCents,
  orderValueCents,
  onChanged,
}: {
  projectId: string;
  entries: TimeEntryWithContext[];
  budgetCents: number | null;
  orderValueCents: number;
  onChanged: () => void;
}) {
  const [minuten, setMinuten] = useState('');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const gesamt = entries.reduce((n, e) => n + e.minutes, 0);

  const eintragen = async () => {
    const wert = Number.parseInt(minuten, 10);
    if (!Number.isInteger(wert) || wert <= 0) {
      setFehler('Bitte Minuten als ganze Zahl über null eintragen.');
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      await crm.timeEntries.create({
        projectId,
        minutes: wert,
        note: notiz.trim() || null,
      });
      setMinuten('');
      setNotiz('');
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Eintragen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (entryId: string) => {
    setFehler(null);
    try {
      await crm.timeEntries.delete(entryId);
      onChanged();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    }
  };

  return (
    <Card className="p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Clock className="h-4 w-4 text-muted-foreground" />
        Zeit &amp; Budget
      </h2>

      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Erfasst</dt>
          <dd className="tabular-nums">{dauer(gesamt)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Beauftragt</dt>
          <dd className="tabular-nums">{formatEuro(orderValueCents)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Budget</dt>
          <dd
            className={`tabular-nums ${
              budgetCents !== null && orderValueCents > budgetCents
                ? 'font-medium text-danger'
                : ''
            }`}
          >
            {budgetCents === null ? 'nicht gesetzt' : formatEuro(budgetCents)}
          </dd>
        </div>
      </dl>

      {entries.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-border pt-2">
          {entries.slice(0, 6).map((e) => (
            <li key={e.id} className="flex items-start gap-2 text-xs">
              <span className="w-14 shrink-0 tabular-nums text-muted-foreground">
                {dauer(e.minutes)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block">{e.note ?? 'ohne Notiz'}</span>
                <span className="block text-muted-foreground">
                  {formatDate(e.happenedOn)}
                  {e.userName && ` · ${e.userName}`}
                </span>
              </span>
              <button
                type="button"
                onClick={() => void loeschen(e.id)}
                className="text-muted-foreground hover:text-danger"
                aria-label="Eintrag löschen"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
          {entries.length > 6 && (
            <li className="text-xs text-muted-foreground">
              … und {entries.length - 6} weitere
            </li>
          )}
        </ul>
      )}

      {/* Zwei Zeilen statt einer: in der schmalen Seitenleiste blieben neben
          Minutenfeld und Knopf nur noch ein paar Pixel fuer „Woran?“. */}
      <div className="mt-3 space-y-2">
        <Input
          className="h-8 text-sm"
          value={notiz}
          onChange={(e) => setNotiz(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            void eintragen();
          }}
          placeholder="Woran?"
          disabled={busy}
        />
        <div className="flex gap-2">
          <Input
            className="h-8 flex-1 text-sm"
            value={minuten}
            onChange={(e) => setMinuten(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              void eintragen();
            }}
            placeholder="Minuten"
            inputMode="numeric"
            aria-label="Minuten"
            disabled={busy}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            onClick={() => void eintragen()}
            disabled={busy || !minuten.trim()}
          >
            Buchen
          </Button>
        </div>
      </div>
      {fehler && <p className="mt-2 text-xs text-danger">{fehler}</p>}
    </Card>
  );
}

/** 135 → „2:15 h“, 45 → „45 min“. */
function dauer(minuten: number): string {
  if (minuten < 60) return `${minuten} min`;
  const stunden = Math.floor(minuten / 60);
  const rest = minuten % 60;
  return `${stunden}:${String(rest).padStart(2, '0')} h`;
}
